import React, { FC } from 'react'
import { Category, FilterType, ListingProducts } from '@/network/graphql.g'
import { NextRouter } from 'next/router'
import Box from '@/components/atoms/Grid/Box'
import { useActiveFilterDispatch } from '@/providers/filterProvider/filtersProvider'
import { FilterQueryItem } from '@/types/filterQueryItem'
import CategoryMenuCampaignRedesign from '@/components/organisms/contentCategoryMenu/categoryMenuCampaignRedesign'
import styled from 'styled-components'
import { validFor } from '@/theme/validFor'

type Props = {
  categoryTree: Category
  router: NextRouter
  campaignId: string
  campaignProducts: ListingProducts
}

export const CategoryTreeCampaignData: FC<Props> = ({
  campaignProducts,
  categoryTree,
  router,
  campaignId
}) => {
  function filterCategory(
    node: Category | null | undefined,
    filterIds: string[]
  ): Category | null {
    if (!node) {
      return null
    }

    const filteredChildren = (node.children ?? [])
      .map((child) => filterCategory(child, filterIds))
      .filter((c): c is Category => Boolean(c))

    if (filterIds.includes(node.id)) {
      return {
        ...node,
        children: filteredChildren
      }
    }
    if (filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren
      }
    }
    return null
  }

  const categoryFilter = campaignProducts.filter.find(
    (item) => item.filterType === FilterType.Category2
  )

  const categoryFromFilter = categoryFilter
    ? categoryFilter.filterItems[0].items
    : []

  const filterIds = categoryFromFilter
    .map((item) => item.id)
    .filter((id): id is string => Boolean(id))

  const filteredTree = filterCategory(categoryTree, filterIds)

  if (!filteredTree) {
    return null
  }

  function getCategoryIdFromQuery(query: NextRouter['query']): string | null {
    if ('category[]' in query) {
      const val = query['category[]']
      return Array.isArray(val) ? val[0] : val
    }
    if ('category' in query) {
      const val = query['category']
      return Array.isArray(val) ? val[0] : val
    }
    return null
  }

  const query = router.query

  const categoryId = getCategoryIdFromQuery(query) || ''

  function filterTreeToLevel(
    node: Category,
    maxLevel: number,
    level = 1
  ): Category {
    return {
      ...node,
      children:
        level < maxLevel && node?.children
          ? node.children.map((child) =>
              filterTreeToLevel(child, maxLevel, level + 1)
            )
          : []
    }
  }

  const levelCategory = 3

  const filteredTreeByLevel = filterTreeToLevel(filteredTree, levelCategory)

  function findCategoryLevel(
    node: Category,
    categoryId: string | null,
    currentLevel = 0
  ): number {
    if (categoryId === null) {
      return -1
    }
    if (node?.id === categoryId) {
      return currentLevel
    }
    if (!node?.children) {
      return -1
    }
    for (const child of node.children) {
      const result = findCategoryLevel(child, categoryId, currentLevel + 1)
      if (result !== -1) {
        return result
      }
    }
    return -1
  }

  function filterCategoryStructure(
    categories: Category[],
    targetId: string
  ): Category[] {
    function findPath(
      items: Category[],
      targetId: string,
      path: Category[] = []
    ): Category[] | null {
      for (const item of items) {
        const currentPath = [...path, item]

        if (item.id === targetId) {
          return currentPath
        }

        if (item.children && item.children.length > 0) {
          const found = findPath(item.children, targetId, currentPath)
          if (found) {
            return found
          }
        }
      }
      return null
    }

    function createCategoryWithoutChildren(category: Category): Category {
      return {
        ...category,
        children: []
      }
    }

    function buildFilteredStructure(
      items: Category[],
      path: Category[],
      currentLevel: number
    ): Category[] {
      if (currentLevel >= path.length) {
        return []
      }

      const targetAtThisLevel = path[currentLevel]
      const result: Category[] = []

      for (const item of items) {
        if (item.id === targetAtThisLevel.id) {
          if (currentLevel === path.length - 1) {
            const targetCategory: Category = {
              ...item,
              children: item.children.map((child) =>
                createCategoryWithoutChildren(child)
              )
            }
            result.push(targetCategory)
          } else {
            const parentCategory: Category = {
              ...item,
              children: buildFilteredStructure(
                item.children,
                path,
                currentLevel + 1
              )
            }
            result.push(parentCategory)
          }
        } else {
          if (currentLevel > 0) {
            result.push(createCategoryWithoutChildren(item))
          } else {
            result.push({
              ...item,
              children: item.children.map((child) =>
                createCategoryWithoutChildren(child)
              )
            })
          }
        }
      }

      return result
    }

    const path = findPath(categories, targetId)

    if (!path || path.length === 0) {
      return []
    }

    if (path.length === 1) {
      const targetCategory = path[0]
      const result: Category[] = []

      for (const item of categories) {
        if (item.id === targetCategory.id) {
          result.push({
            ...item,
            children: item.children.map((child) =>
              createCategoryWithoutChildren(child)
            )
          })
        } else {
          result.push({
            ...item,
            children: item.children.map((child) =>
              createCategoryWithoutChildren(child)
            )
          })
        }
      }

      return result
    }

    const filteredStructure = buildFilteredStructure(categories, path, 0)

    return filteredStructure
  }
  const level = findCategoryLevel(filteredTree, categoryId)

  const filteredResult =
    level > 1
      ? filterCategoryStructure(filteredTree.children, categoryId)
      : filteredTreeByLevel.children

  const filterVisibleNavigation = (navigationData: Category[]) => {
    return navigationData.filter((item) => {
      if (item.visibleInNavigation === false) {
        return false
      }

      if (item.children && item.children.length > 0) {
        item.children = item.children.filter(
          (child) => child.visibleInNavigation !== false
        )
      }

      return true
    })
  }

  const filerNavigation = filterVisibleNavigation(filteredResult)

  const disableNavigation =
    Array.isArray(filerNavigation) &&
    filerNavigation.length === 1 &&
    filerNavigation[0]?.children?.length === 0

  const { toggleFilterAndSetUrl } = useActiveFilterDispatch()

  const setCategoryFilter = (categoryId: string) => {
    toggleFilterAndSetUrl(
      categoryId,
      categoryId,
      FilterQueryItem.CATEGORY_CAMPAIGN
    )
  }

  if (disableNavigation) {
    return null
  }

  return (
    <StickyRedesignWrapper>
      <CategoryMenuCampaignRedesign
        categories={filerNavigation}
        campaignId={campaignId}
        router={router}
        setCategoryFilter={setCategoryFilter}
      />
    </StickyRedesignWrapper>
  )
}

const StickyRedesignWrapper = styled(Box)`
  position: sticky;
  top: 150px;
  align-self: flex-start;
  height: fit-content;
  margin-top: 48px;
  display: block;
  ${validFor.tablet`
   display: none;
  `}
  ${validFor.mobile`
   display: none;
  `}
`
